import React, { useState, useEffect, useCallback } from "react";
import { Gift, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, Empty, SectionHeader, Card, fmtDate } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const REG = ["pending", "registered"];
const SUB = ["none", "subscribed"];
const REWARD = ["pending", "eligible", "approved", "paid", "rejected"];

const StatusSelect = ({ value, options, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
  </Select>
);

export default function AdminReferrals() {
  const [config, setConfig] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/referrals");
    setReferrals(data.referrals || []);
    setConfig(data.config);
  }, []);
  useEffect(() => { load().catch(() => setReferrals([])); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/referral/config", config);
      setConfig(data.config);
      toast.success("Configuration de la récompense enregistrée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, field, value) => {
    setReferrals((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    try { await api.patch(`/admin/referrals/${id}`, { [field]: value }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); load(); }
  };

  if (referrals === null) return <Loading />;

  return (
    <div data-testid="admin-referrals" className="space-y-6">
      <SectionHeader title="Referrals" subtitle="Programme de parrainage — configuration et suivi (paiements manuels)." />

      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="reward-config">
        <div className="mb-4 flex items-center gap-2"><Gift size={18} className="text-[#3E7256]" /><h3 className="font-heading text-lg font-bold text-[#17201A]">Configuration de la récompense</h3></div>
        {config && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Type</label>
              <Select value={config.reward_type} onValueChange={(v) => setConfig({ ...config, reward_type: v })}>
                <SelectTrigger className="h-10 text-sm" data-testid="config-reward-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="amount">Montant</SelectItem><SelectItem value="benefit">Avantage</SelectItem></SelectContent>
              </Select>
            </div>
            {config.reward_type === "amount" ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Montant</label>
                  <input data-testid="config-amount" type="number" value={config.amount} onChange={(e) => setConfig({ ...config, amount: parseFloat(e.target.value) || 0 })} className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Devise</label>
                  <input data-testid="config-currency" value={config.currency} onChange={(e) => setConfig({ ...config, currency: e.target.value })} className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]" />
                </div>
              </>
            ) : (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Description de l'avantage</label>
                <input data-testid="config-benefit" value={config.benefit_description} onChange={(e) => setConfig({ ...config, benefit_description: e.target.value })} placeholder="ex. 1 mois d'abonnement offert" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]" />
              </div>
            )}
            <div className="flex items-center gap-2 sm:col-span-2">
              <input data-testid="config-enabled" type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} className="h-4 w-4 accent-[#3E7256]" />
              <span className="text-sm text-[#374151]">Programme de parrainage activé</span>
            </div>
            <div className="sm:col-span-2">
              <button data-testid="save-config" onClick={saveConfig} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-50">
                <Save size={15} /> Enregistrer la configuration
              </button>
            </div>
          </div>
        )}
      </section>

      <section data-testid="admin-referrals-table">
        <h3 className="mb-4 font-heading text-lg font-bold text-[#17201A]">Tous les parrainages</h3>
        {referrals.length === 0 ? <Empty label="Aucun parrainage enregistré." testid="admin-referrals-empty" /> : (
          <Card>
            <SelectableTable rows={referrals} rowId={(r) => r.id} filename="adminreferrals.csv" ariaLabel="Tableau — sélection et défilement">
              <table className="w-full min-w-[900px]">
                <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-3 py-2"><SelectAllHeaderCheckbox /></th>
                  <th className="px-5 py-3">Parrain</th><th className="px-5 py-3">Invité</th><th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Inscription</th><th className="px-5 py-3">Abonnement</th><th className="px-5 py-3">Récompense</th>
                </tr></thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r.id} data-testid={`admin-referral-${r.id}`} className="border-b border-slate-100 last:border-0"><td className="px-3 py-2"><RowCheckbox id={r.id} /></td>
                      <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{r.referring_org?.name || "—"}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.invited_org?.name || r.invited_email}</td>
                      <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(r.invitation_date)}</td>
                      <td className="px-5 py-3"><StatusSelect value={r.registration_status} options={REG} onChange={(v) => updateStatus(r.id, "registration_status", v)} testid={`reg-${r.id}`} /></td>
                      <td className="px-5 py-3"><StatusSelect value={r.subscription_status} options={SUB} onChange={(v) => updateStatus(r.id, "subscription_status", v)} testid={`sub-${r.id}`} /></td>
                      <td className="px-5 py-3"><StatusSelect value={r.reward_status} options={REWARD} onChange={(v) => updateStatus(r.id, "reward_status", v)} testid={`reward-${r.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SelectableTable>
          </Card>
        )}
      </section>
    </div>
  );
}
